import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Gem } from "lucide-react";
import api from "../../api";
import { toast } from "../../hooks/use-toast";

const num = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]";

function PlanCard({ plan, features, count, isDefault, onChange, onRemove, onDefault }) {
  const set = (k, v) => onChange({ ...plan, [k]: v });
  const toggleFeat = (k) => set("features", plan.features.includes(k) ? plan.features.filter((x) => x !== k) : [...plan.features, k]);
  return (
    <div className={`bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)] ${plan.active ? "" : "opacity-60"}`} data-testid={`admin-plan-${plan.id || "new"}`}>
      <div className="flex items-start justify-between gap-2">
        <input value={plan.name} onChange={(e) => set("name", e.target.value)} placeholder="Plan name" data-testid="admin-plan-name"
          className="font-bold text-lg text-[#16243b] outline-none border-b border-transparent focus:border-[#2ec9c9] w-full" />
        <button onClick={onRemove} disabled={count > 0} title={count > 0 ? "Move businesses off this plan first" : "Delete plan"} data-testid="admin-plan-remove" className="text-red-400 hover:text-red-600 disabled:opacity-30"><Trash2 size={16} /></button>
      </div>
      <input value={plan.tagline || ""} onChange={(e) => set("tagline", e.target.value)} placeholder="Short tagline" className="text-sm text-[#8a97a8] outline-none w-full mt-1" />
      <p className="text-xs text-[#8a97a8] mt-1">{count} business{count === 1 ? "" : "es"} on this plan</p>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {[["Monthly £", "monthly_fee"], ["Commission %", "commission_pct"], ["Per sale £", "commission_fixed"]].map(([l, k]) => (
          <label key={k} className="text-xs text-[#5a6b80]">{l}
            <input type="number" step="0.01" min="0" value={plan[k]} onChange={(e) => set(k, e.target.value)} data-testid={`admin-plan-${k}`} className={num} />
          </label>
        ))}
      </div>
      <p className="text-xs font-semibold text-[#16243b] mt-4 mb-2">Included features</p>
      <div className="space-y-1.5">
        {features.map((f) => (
          <label key={f.key} className="flex items-center gap-2 text-sm text-[#16243b]">
            <input type="checkbox" checked={plan.features.includes(f.key)} onChange={() => toggleFeat(f.key)} data-testid={`admin-plan-feature-${f.key}`} /> {f.label}
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={plan.active} onChange={(e) => set("active", e.target.checked)} data-testid="admin-plan-active" /> Offered to businesses</label>
        <label className="flex items-center gap-2 text-xs text-[#5a6b80]"><input type="radio" checked={isDefault} onChange={onDefault} data-testid="admin-plan-default" /> Default for new signups</label>
      </div>
    </div>
  );
}

export default function PlansAdmin() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/plans").then(({ data }) => setCfg(data));
  useEffect(() => { load(); }, []);
  if (!cfg) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const setPlan = (i, p) => setCfg({ ...cfg, plans: cfg.plans.map((x, j) => (j === i ? p : x)) });
  const setFeat = (k, field, val) => setCfg({ ...cfg, feature_config: { ...cfg.feature_config, [k]: { ...cfg.feature_config[k], [field]: val } } });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/plans", { plans: cfg.plans, feature_config: cfg.feature_config, default_plan_id: cfg.default_plan_id, trial_days: parseInt(cfg.trial_days) || 0, trial_plan_id: cfg.trial_plan_id });
      setCfg(data);
      toast({ title: "Plans saved" });
    } catch (err) {
      toast({ title: "Could not save", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="plans-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#5a6b80] flex items-center gap-2"><Gem size={16} className="text-[#2ec9c9]" /> Set prices, commission and features for each package. Changes apply to fees on new sales immediately.</p>
        <button onClick={() => setCfg({ ...cfg, plans: [...cfg.plans, { id: "", name: "", tagline: "", monthly_fee: 0, commission_pct: 5, commission_fixed: 0.2, active: true, features: [] }] })}
          data-testid="admin-add-plan" className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"><Plus size={15} /> Add plan</button>
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        {cfg.plans.map((p, i) => (
          <PlanCard key={i} plan={p} features={cfg.features} count={cfg.business_counts[p.id] || 0} isDefault={cfg.default_plan_id === p.id}
            onChange={(np) => setPlan(i, np)} onDefault={() => setCfg({ ...cfg, default_plan_id: p.id })}
            onRemove={() => setCfg({ ...cfg, plans: cfg.plans.filter((_, j) => j !== i) })} />
        ))}
      </div>
      <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)] flex flex-wrap items-end gap-4" data-testid="admin-trial-settings">
        <div className="flex-1 min-w-[220px]">
          <h4 className="font-semibold text-[#16243b]">Free trial for new signups</h4>
          <p className="text-xs text-[#8a97a8]">New businesses start on the trial plan, get a reminder 3 days before it ends, then move to the default plan unless they subscribe. Set 0 days to switch off.</p>
        </div>
        <label className="text-xs text-[#5a6b80]">Trial days
          <input type="number" min="0" max="90" value={cfg.trial_days} onChange={(e) => setCfg({ ...cfg, trial_days: e.target.value })} data-testid="admin-trial-days" className={`${num} w-24`} />
        </label>
        <label className="text-xs text-[#5a6b80]">Trial plan
          <select value={cfg.trial_plan_id} onChange={(e) => setCfg({ ...cfg, trial_plan_id: e.target.value })} data-testid="admin-trial-plan" className={`${num} w-40 bg-white`}>
            {cfg.plans.filter((p) => p.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <button type="button" onClick={async () => { const { data } = await api.post("/admin/run-trials"); toast({ title: "Trials processed", description: `Reminded ${data.reminded}, expired ${data.expired}, converted ${data.converted}` }); }}
          data-testid="admin-run-trials" className="gp-btn-outline rounded-xl px-4 py-2 text-sm font-medium">Run trial check now</button>
      </div>
      <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="admin-feature-switches">
        <h4 className="font-semibold text-[#16243b]">Feature switches</h4>
        <p className="text-xs text-[#8a97a8] mb-3">Turn a feature off for everyone, or mark it "Coming soon" (shown on plans but not usable yet).</p>
        <div className="divide-y divide-gray-50">
          {cfg.features.map((f) => (
            <div key={f.key} className="flex flex-wrap items-center justify-between gap-3 py-2.5 text-sm">
              <span><span className="font-medium text-[#16243b]">{f.label}</span> <span className="text-xs text-[#8a97a8]">— {f.description}</span></span>
              <span className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={cfg.feature_config[f.key].coming_soon} onChange={(e) => setFeat(f.key, "coming_soon", e.target.checked)} data-testid={`feature-coming-soon-${f.key}`} /> Coming soon</label>
                <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={cfg.feature_config[f.key].enabled} onChange={(e) => setFeat(f.key, "enabled", e.target.checked)} data-testid={`feature-enabled-${f.key}`} /> On</label>
              </span>
            </div>
          ))}
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="admin-save-plans" className="gp-btn-teal rounded-xl px-6 py-3 font-medium disabled:opacity-60">{saving ? "Saving…" : "Save plans"}</button>
    </div>
  );
}
